/**
 * RfpChatPanel — multi-turn conversational chat scoped to one RFP.
 *
 * Each message carries the full conversation history so the backend
 * can answer follow-up questions in context (e.g. "tell me more",
 * "what about the deadline for that?").
 *
 * Props:
 *   rfpId  — UUID of the RFP to scope the search to (required)
 *   rfp    — optional RFP object for display (title etc.)
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Bot, User as UserIcon, Send, Loader2, FileText,
  RotateCcw, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { v2Api } from "@/lib/v2Api";

const EXAMPLES = [
  "What is the EMD amount and when is it due?",
  "What are the key eligibility criteria?",
  "Summarise the BOQ and quantity requirements.",
  "What are all the submission deadlines?",
  "Is there a performance bank guarantee requirement?",
];

function CitationCard({ c, index }) {
  const open = () => {
    if (!c.rfp_id) return;
    v2Api.rfp.openFileInNewTab(c.rfp_id, c.page).catch(() => {
      toast.error("Could not open source PDF");
    });
  };

  return (
    <button
      type="button"
      onClick={open}
      className="w-full text-left flex items-start gap-2 px-2 py-1.5 rounded-md border border-slate-100 hover:border-blue-300 hover:bg-blue-50 transition-colors group"
      title={`Open ${c.rfp_title || "RFP"} at page ${c.page}`}
    >
      <span className="font-mono text-[10px] text-slate-400 mt-0.5 shrink-0 group-hover:text-blue-500">
        [{index + 1}]
      </span>
      <FileText className="w-3 h-3 mt-0.5 text-slate-300 shrink-0 group-hover:text-blue-400" />
      <div className="min-w-0 flex-1">
        <div className="text-xs text-blue-600 font-medium truncate group-hover:underline">
          p.{c.page} · {c.rfp_title || "RFP"} ↗
        </div>
        <div className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{c.snippet}</div>
      </div>
    </button>
  );
}

function Message({ msg }) {
  const [citationsOpen, setCitationsOpen] = useState(false);
  const isUser = msg.role === "user";

  if (isUser) {
    return (
      <div className="flex gap-2 justify-end">
        <div className="max-w-[80%] bg-blue-600 text-white rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm">
          {msg.content}
        </div>
        <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center shrink-0 mt-0.5">
          <UserIcon className="w-4 h-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2 justify-start">
      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 mt-0.5">
        <Bot className="w-4 h-4" />
      </div>
      <div className="max-w-[85%] space-y-2">
        <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm text-slate-800 whitespace-pre-wrap shadow-sm">
          {msg.content}
        </div>
        {msg.citations && msg.citations.length > 0 && (
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => setCitationsOpen((v) => !v)}
              className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-600 font-medium"
            >
              <ChevronDown
                className={`w-3 h-3 transition-transform ${citationsOpen ? "rotate-180" : ""}`}
              />
              {msg.citations.length} source{msg.citations.length > 1 ? "s" : ""}
            </button>
            {citationsOpen && (
              <div className="space-y-1 pl-1">
                {msg.citations.map((c, j) => (
                  <CitationCard key={j} c={c} index={j} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const _storageKey = (rfpId) => `rfp-chat-${rfpId}`;

function _loadMessages(rfpId) {
  try {
    const raw = sessionStorage.getItem(_storageKey(rfpId));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function _saveMessages(rfpId, msgs) {
  try { sessionStorage.setItem(_storageKey(rfpId), JSON.stringify(msgs)); } catch {}
}

export default function RfpChatPanel({ rfpId, rfp }) {
  const [messages, setMessages] = useState(() => _loadMessages(rfpId));
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Reload from storage when rfpId changes (user opens a different tender)
  useEffect(() => {
    setMessages(_loadMessages(rfpId));
  }, [rfpId]);

  // Persist messages whenever they change
  useEffect(() => {
    _saveMessages(rfpId, messages);
  }, [rfpId, messages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (text) => {
    const q = (text || draft).trim();
    if (!q || busy) return;
    setDraft("");

    const userMsg = { role: "user", content: q };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setBusy(true);

    try {
      // Build history from all messages BEFORE this new one (the backend
      // expects prior turns, not the current query).
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const res = await v2Api.chat({
        query: q,
        rfp_id: rfpId,
        k: 8,
        history,
      });
      setMessages([
        ...nextMessages,
        {
          role: "assistant",
          content: res.answer || "(no answer)",
          citations: res.citations || [],
          model: res.model,
        },
      ]);
    } catch (err) {
      setMessages([
        ...nextMessages,
        {
          role: "assistant",
          content:
            err?.response?.data?.detail ||
            err?.message ||
            "Something went wrong. Try again.",
          citations: [],
          model: "error",
        },
      ]);
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const reset = () => {
    setMessages([]);
    setDraft("");
    try { sessionStorage.removeItem(_storageKey(rfpId)); } catch {}
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-blue-600" />
          <div>
            <p className="text-sm font-semibold text-slate-800">Ask this RFP</p>
            {rfp?.title && (
              <p className="text-[10px] text-slate-400 truncate max-w-[280px]" title={rfp.title}>
                {rfp.title}
              </p>
            )}
          </div>
        </div>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={reset}
            className="text-slate-400 hover:text-slate-700 inline-flex items-center gap-1 text-xs"
            title="Clear conversation"
          >
            <RotateCcw className="w-3 h-3" /> New chat
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {messages.length === 0 && (
          <div className="space-y-4 py-4">
            <div className="text-center space-y-1">
              <Bot className="w-8 h-8 text-blue-200 mx-auto" />
              <p className="text-sm text-slate-500 font-medium">
                Ask anything about this tender
              </p>
              <p className="text-xs text-slate-400">
                AI answers are grounded in the uploaded RFP document.
              </p>
            </div>
            <div className="space-y-2">
              {EXAMPLES.map((ex, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => send(ex)}
                  className="w-full text-left text-xs px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50 text-slate-700 transition-colors"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <Message key={i} msg={m} />
        ))}

        {busy && (
          <div className="flex gap-2 justify-start">
            <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
              <Bot className="w-4 h-4" />
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-2.5 shadow-sm flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
              <span className="text-sm text-slate-400">Searching…</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t bg-white">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            placeholder="Ask a question about this RFP…"
            disabled={busy}
            rows={1}
            className="flex-1 resize-none px-3 py-2.5 text-sm border border-slate-300 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 disabled:bg-slate-50 disabled:text-slate-400 leading-5 max-h-32 overflow-y-auto"
            style={{ minHeight: "42px" }}
            onInput={(e) => {
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 128) + "px";
            }}
          />
          <button
            type="button"
            onClick={() => send()}
            disabled={busy || !draft.trim()}
            className="shrink-0 w-10 h-10 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white flex items-center justify-center transition-colors"
            title="Send (Enter)"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5 text-center">
          Answers grounded in RFP content · Enter to send · Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
